import { BarChart3, ClipboardList, FileSearch, LogOut, Settings, Tags } from 'lucide-react';
import type { SecCapReasonView } from '../store';

interface ReasonHeaderProps {
  view: SecCapReasonView;
  operator: string;
  isAdmin: boolean;
  environment?: string;
  workerVersion?: string;
  onNavigate: (view: SecCapReasonView) => void;
  onSignOut?: () => void;
}

const navItems: Array<{
  view: SecCapReasonView;
  label: string;
  icon: typeof ClipboardList;
  adminOnly?: boolean;
}> = [
  { view: 'tasks', label: 'My Tasks', icon: ClipboardList },
  { view: 'leaderboard', label: 'Leaderboard', icon: BarChart3, adminOnly: true },
  { view: 'results', label: 'Results', icon: FileSearch, adminOnly: true },
  { view: 'admin', label: 'Admin', icon: Settings, adminOnly: true },
];

export function ReasonHeader({
  view,
  operator,
  isAdmin,
  environment = 'unknown',
  workerVersion = 'unknown',
  onNavigate,
  onSignOut,
}: ReasonHeaderProps) {
  return (
    <header className="border-b border-brand-border bg-brand-dark/95">
      <div className="mx-auto flex min-h-16 max-w-[1600px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-6">
        <button
          type="button"
          onClick={() => onNavigate('tasks')}
          className="flex items-center gap-3 text-left"
          aria-label="Open My Tasks"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-orange text-white">
            <Tags className="h-5 w-5" />
          </span>
          <span>
            <span className="block text-base font-semibold text-brand-text-primary">
              SecCapReason
            </span>
            <span className="block text-xs text-brand-text-secondary">Reason annotation</span>
          </span>
          <span
            title={`Environment: ${environment}\nWorker: ${workerVersion}`}
            className={`border px-1.5 py-0.5 font-mono text-[10px] font-semibold uppercase ${
              environment === 'production'
                ? 'border-emerald-500/50 text-emerald-300'
                : environment === 'staging'
                  ? 'border-amber-500/50 text-amber-300'
                  : 'border-brand-border text-brand-text-secondary'
            }`}
          >
            {environment === 'production' ? 'PROD' : environment === 'staging' ? 'TEST' : 'LOCAL'}
          </span>
        </button>

        <nav className="flex items-center gap-1" aria-label="SecCapReason navigation">
          {navItems.map((item) => {
            if (item.adminOnly && !isAdmin) return null;
            const Icon = item.icon;
            const active =
              view === item.view ||
              (item.view === 'tasks' && view === 'editor') ||
              (item.view === 'results' && view === 'result');
            return (
              <button
                key={item.view}
                type="button"
                onClick={() => onNavigate(item.view)}
                aria-label={item.label}
                title={item.label}
                className={`inline-flex h-9 items-center gap-2 rounded-md px-3 text-sm transition ${
                  active
                    ? 'bg-brand-surface-highlight text-brand-text-primary'
                    : 'text-brand-text-secondary hover:bg-brand-surface hover:text-brand-text-primary'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden sm:inline">{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 text-sm text-brand-text-secondary">
          <span className="max-w-44 truncate font-mono text-xs" title={operator}>
            {operator}
          </span>
          {onSignOut ? (
            <button
              type="button"
              onClick={onSignOut}
              className="flex h-9 w-9 items-center justify-center rounded-md border border-brand-border hover:text-brand-text-primary"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </button>
          ) : null}
        </div>
      </div>
    </header>
  );
}
